/** @jsxImportSource @emotion/react */

// 회계 검수 › 일반전표 상세 (세특 일반전표 화면기획 v1.1).
// 전표는 PRO MCP가 등록하므로 이 화면은 조회·보류 처리·삭제·세무사랑 전송·마감만 한다(편집 없음).
// 화면 구성은 기타매입·매출(pages/Review)과 같게 맞춘다.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { css } from "@emotion/react";
import { DialogContentText, Tooltip } from "@mui/material";

import {
  HALF_AND_QUARTERS,
  HalfYear,
  MONTHS,
  Quarter,
  YearMonth
} from "data/period.data";
import { Business } from "models/business.model";
import {
  Journal,
  JournalNewzenFilter,
  JournalSummary,
  ResJournalList,
  ResJournalRelease
} from "models/journal.model";

import Table from "components/common/Table";
import BaseSelect from "components/common/Select/Base";
import BaseInput from "components/common/Input/Base";
import BaseButton from "components/common/Button/Base";
import SubtitleCard from "components/common/Card/Subtitle/SubtitleCard";
import AlertDialog, { AlertData } from "components/common/Dialog/Alert";
import ConfirmDialog from "components/common/Dialog/Confirm";
import NewzenSendDialog from "pages/Journal/Dialog/NewzenSend";
import { JOURNAL_COLS, JournalRow } from "pages/Journal/column.data";

import {
  deleteJournals,
  getJournals,
  getStores,
  releaseJournals,
  updateJournalConfirmation
} from "pages/remotes";
import {
  convertCurrency,
  convertDateToPeriod,
  convertQuarterAndHalfToDate,
  convertYearMonthToDate,
  reverseArray,
  sortList
} from "utils/converter";
import { PALETTE } from "styles/base/variables";
import {
  accountLabel,
  AccountNameMap,
  CONFIRMED_SELECTION_MESSAGE,
  holdReasonLabel,
  isDeletable,
  isSendable,
  journalStatus,
  linesBySide,
  matchesKeyword,
  memoText,
  PAGE_SIZE,
  releaseSkipLabel,
  SENT_DELETE_MESSAGE,
  sumBy,
  toJournalRow
} from "pages/Journal/journal.logic";

const EMPTY_SUMMARY: JournalSummary = {
  count: 0,
  totalDebit: 0,
  totalCredit: 0,
  unsent: 0,
  unsentAmount: 0,
  held: 0,
  sent: 0,
  failed: 0
};

const STATUS_FILTER_OPTIONS: { value: JournalNewzenFilter; label: string }[] = [
  { value: "all", label: "전체 상태" },
  { value: "unsent", label: "미전송" },
  { value: "held", label: "보류" },
  { value: "sent", label: "전송" },
  { value: "failed", label: "전송 실패" }
];

const ALL_ACCOUNTS = "all";

const errorMessage = (err: any, fallback: string) =>
  err.response?.data?.message || fallback;

function JournalReview() {
  const navigate = useNavigate();
  const { id: bizId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [spStartDate, spEndDate, spSort] = [
    searchParams.get("startDate"),
    searchParams.get("endDate"),
    searchParams.get("sort")
  ];

  const [business, setBusiness] = useState<Business | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [journals, setJournals] = useState<Journal[]>([]);
  const [summary, setSummary] = useState<JournalSummary>(EMPTY_SUMMARY);
  // 계정 필터를 걸어도 선택지가 줄지 않도록 받은 계정을 계속 모아 둔다
  const [accountNames, setAccountNames] = useState<AccountNameMap>(new Map());

  const [statusFilter, setStatusFilter] = useState<JournalNewzenFilter>("all");
  const [accountFilter, setAccountFilter] = useState(ALL_ACCOUNTS);
  const [keyword, setKeyword] = useState("");
  const [isShowSelectedOnly, setIsShowSelectedOnly] = useState(false);
  const [page, setPage] = useState(0);

  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertData, setAlertData] = useState<AlertData>({ message: "" });
  const [deleteTargetIds, setDeleteTargetIds] = useState<string[]>([]);
  const [isBusy, setIsBusy] = useState(false); // 삭제·보류 해제·마감 요청 중
  const [isSendOpen, setIsSendOpen] = useState(false);

  const showAlert = (message: React.ReactNode, type: AlertData["type"]) => {
    setAlertData({ message, type });
    setIsAlertOpen(true);
  };

  // 기간이 없으면 당월로 시작
  useEffect(() => {
    if (spStartDate && spEndDate) return;
    const now = new Date();
    const yearMonth = `${now.getFullYear()}-${String(
      now.getMonth() + 1
    ).padStart(2, "0")}` as YearMonth;
    const { startDate, endDate } = convertYearMonthToDate(yearMonth);
    searchParams.set("startDate", startDate);
    searchParams.set("endDate", endDate);
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadBusiness();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bizId, spStartDate, spEndDate]);

  useEffect(() => {
    loadJournals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bizId, spStartDate, spEndDate, statusFilter, accountFilter]);

  // 일반전표 조회 응답에는 사업자 정보가 없어 사업자 목록(3.0)에서 찾는다.
  // 기간 단위 마감 여부(isAccountingConfirmed)도 여기서 받는다. 실패해도 목록은 동작
  const loadBusiness = () => {
    if (!bizId || !spStartDate || !spEndDate) return;
    getStores("journal", { startDate: spStartDate, endDate: spEndDate })
      .then((res) => {
        const found = (res.data?.businessAccountingInfos || []).find(
          (biz: Business) => biz.id === bizId
        );
        setBusiness(found || null);
      })
      .catch((err) => console.error(err));
  };

  const loadJournals = () => {
    if (!bizId || !spStartDate || !spEndDate) return;
    setIsLoading(true);
    getJournals(bizId, {
      startDate: spStartDate,
      endDate: spEndDate,
      newzen: statusFilter,
      ...(accountFilter !== ALL_ACCOUNTS ? { accountCode: accountFilter } : {})
    })
      .then((res) => {
        const data: ResJournalList = res.data;
        setJournals(data?.journals || []);
        setSummary(data?.summary || EMPTY_SUMMARY);
        setAccountNames((prev) => {
          const next = new Map(prev);
          (data?.accountCodes || []).forEach((ac) =>
            next.set(ac.code, ac.name)
          );
          return next;
        });
        setCheckedIds(new Set());
        setIsShowSelectedOnly(false);
        setPage(0);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        setJournals([]);
        setSummary(EMPTY_SUMMARY);
        showAlert(
          errorMessage(err, "일반전표를 불러오던 중 오류가 발생했습니다."),
          "error"
        );
      });
  };

  const setPeriod = (startDate: string, endDate: string) => {
    if (!(startDate && endDate)) return;
    searchParams.set("startDate", startDate);
    searchParams.set("endDate", endDate);
    setSearchParams(searchParams);
  };
  const onSelectQuarter = (e: React.MouseEvent<HTMLOptionElement>) => {
    const { startDate, endDate } = convertQuarterAndHalfToDate(
      e.currentTarget.value as Quarter | HalfYear
    );
    setPeriod(startDate, endDate);
  };
  const onSelectMonth = (e: React.MouseEvent<HTMLOptionElement>) => {
    const { startDate, endDate } = convertYearMonthToDate(
      e.currentTarget.value as YearMonth
    );
    setPeriod(startDate, endDate);
  };

  // 요약 칩을 누르면 그 상태만 보고, 다시 누르면 전체로 돌아간다
  const toggleStatusFilter = (status: JournalNewzenFilter) => () =>
    setStatusFilter((prev) => (prev === status ? "all" : status));

  const rows: JournalRow[] = useMemo(() => {
    const filtered = journals.filter(
      (journal) =>
        matchesKeyword(journal, keyword) &&
        (!isShowSelectedOnly || checkedIds.has(journal.id))
    );
    return sortList(
      filtered.map((journal) => toJournalRow(journal, accountNames)),
      spSort
    );
  }, [journals, keyword, isShowSelectedOnly, checkedIds, accountNames, spSort]);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  // 선택만 보기 등으로 행이 줄면 마지막 페이지로 당긴다
  const currentPage = Math.min(page, pageCount - 1);
  const pageRows = rows.slice(
    currentPage * PAGE_SIZE,
    (currentPage + 1) * PAGE_SIZE
  );
  // 검색어·선택만 보기는 프런트 필터라 서버 summary 대신 보이는 행으로 합계를 낸다
  const totalDebit = sumBy(rows, "totalDebit");
  const totalCredit = sumBy(rows, "totalCredit");

  const selectedJournals = journals.filter((journal) =>
    checkedIds.has(journal.id)
  );
  const hasConfirmedSelected = selectedJournals.some(
    (journal) => journal.isAccountingConfirmed
  );
  const sendableCount = selectedJournals.filter(isSendable).length;
  const allHeldSelected =
    selectedJournals.length > 0 &&
    selectedJournals.every((journal) => journalStatus(journal) === "held");
  const allDeletableSelected =
    selectedJournals.length > 0 && selectedJournals.every(isDeletable);

  const sendDisabledReason = !selectedJournals.length
    ? "전송할 전표를 선택해주세요."
    : hasConfirmedSelected
      ? CONFIRMED_SELECTION_MESSAGE
      : !sendableCount
        ? "선택한 전표 중 미전송 전표가 없습니다."
        : "";
  const releaseDisabledReason = !selectedJournals.length
    ? "보류를 해제할 전표를 선택해주세요."
    : hasConfirmedSelected
      ? CONFIRMED_SELECTION_MESSAGE
      : !allHeldSelected
        ? "보류 전표만 선택했을 때 해제할 수 있습니다."
        : "";
  const deleteDisabledReason = !selectedJournals.length
    ? "삭제할 데이터를 선택해주세요."
    : hasConfirmedSelected
      ? CONFIRMED_SELECTION_MESSAGE
      : !allDeletableSelected
        ? SENT_DELETE_MESSAGE
        : "";

  // 마감 여부는 사업자 목록(3.0)의 기간 단위 값. 목록을 못 받았으면 받은 전표로 판단한다
  const isPeriodConfirmed = business
    ? business.isAccountingConfirmed
    : journals.length > 0 &&
      journals.every((journal) => journal.isAccountingConfirmed);

  const toggleExpanded = (data: JournalRow) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(data.id)) next.delete(data.id);
      else next.add(data.id);
      return next;
    });
  };

  const release = (ids: string[]) => {
    if (!bizId || !ids.length) return;
    setIsBusy(true);
    releaseJournals(bizId, ids)
      .then((res) => {
        setIsBusy(false);
        const results: ResJournalRelease = res.data || [];
        const released = results.filter((r) => r.result === "released");
        const skipped = results.filter((r) => r.result === "skipped");
        showAlert(
          `보류 해제 ${released.length}건` +
            (skipped.length
              ? ` · 건너뜀 ${skipped.length}건 (${skipped
                  .map((r) => releaseSkipLabel(r.reason))
                  .filter(Boolean)
                  .join(", ")})`
              : "") +
            "\n세무사랑 전송은 따로 해주세요.",
          skipped.length ? "warning" : "success"
        );
        loadBusiness();
        loadJournals();
      })
      .catch((err) => {
        setIsBusy(false);
        showAlert(
          errorMessage(err, "보류 해제 중 오류가 발생했습니다."),
          "error"
        );
      });
  };

  const confirmDelete = () => {
    if (!bizId || !deleteTargetIds.length) return;
    setIsBusy(true);
    deleteJournals(bizId, deleteTargetIds)
      .then(() => {
        setIsBusy(false);
        setDeleteTargetIds([]);
        showAlert("선택한 데이터가 삭제되었습니다.", "success");
        loadBusiness();
        loadJournals();
      })
      .catch((err) => {
        setIsBusy(false);
        setDeleteTargetIds([]);
        // JE001: detail.ids에 막힌 전표 id가 온다
        const blockedIds: string[] = err.response?.data?.detail?.ids || [];
        const blocked = journals.filter((journal) =>
          blockedIds.includes(journal.id)
        );
        showAlert(
          errorMessage(err, "삭제 중 오류가 발생했습니다.") +
            (blocked.length
              ? `\n\n${blocked
                  .map((journal) => `· ${journal.makeDt} ${memoText(journal)}`)
                  .join("\n")}`
              : ""),
          "error"
        );
      });
  };

  const toggleConfirmation = () => {
    if (!bizId || !spStartDate || !spEndDate) return;
    const nextConfirmed = !isPeriodConfirmed;
    const label = nextConfirmed ? "마감 설정" : "마감 해제";
    setIsBusy(true);
    updateJournalConfirmation(bizId, nextConfirmed, {
      startDate: spStartDate,
      endDate: spEndDate
    })
      .then(() => {
        setIsBusy(false);
        showAlert(`${label}되었습니다.`, "success");
        loadBusiness();
        loadJournals();
      })
      .catch((err) => {
        setIsBusy(false);
        showAlert(
          errorMessage(err, `${label} 중 오류가 발생했습니다.`),
          "error"
        );
      });
  };

  const toDashboard = () => {
    navigate(
      spStartDate && spEndDate
        ? `/journal?startDate=${spStartDate}&endDate=${spEndDate}`
        : "/journal"
    );
  };

  const reviewLink = (menuType: string) =>
    `/review/${menuType}/${bizId}` +
    (spStartDate && spEndDate
      ? `?startDate=${spStartDate}&endDate=${spEndDate}`
      : "");

  const isBalancedTotal = totalDebit === totalCredit;
  const actionDisabled = isLoading || isBusy;

  // 기존 탭처럼 처리할 건(보류)이 남아 있으면 마감할 수 없다. 해제는 언제나 가능
  const heldCount = business?.heldDataNumber ?? summary.held;
  const confirmDisabledReason = isPeriodConfirmed
    ? ""
    : !(business ? business.totalDataNumber : journals.length)
      ? "기간 안에 전표가 없습니다."
      : heldCount > 0
        ? `보류 ${heldCount}건을 해제하거나 삭제한 뒤 마감할 수 있습니다.`
        : "";

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 24px;
      `}
    >
      <section
        css={css`
          padding-top: 24px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        `}
      >
        <div>
          <BaseButton
            onClick={toDashboard}
            css={css`
              color: ${PALETTE.textSub};
              padding: 4px;

              &:hover {
                color: ${PALETTE.text};
              }
            `}
          >
            {"< 전체 업체 목록"}
          </BaseButton>
        </div>
        <div
          css={css`
            display: flex;
            align-items: center;
          `}
        >
          <h1>회계 검수</h1>
          {business && (
            <SubtitleCard>
              <div>{business.name}</div>
              <div>{business.regNumber}</div>
              {business.cluster && (
                <div>
                  {business.cluster}{" "}
                  {business.subCluster && <span>/ {business.subCluster}</span>}
                </div>
              )}
            </SubtitleCard>
          )}
        </div>
      </section>

      <section>
        <div
          css={css`
            margin-bottom: 10px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
            `}
          >
            <BaseSelect
              name="반기/분기"
              options={reverseArray(HALF_AND_QUARTERS)}
              selectedOption={convertDateToPeriod(spStartDate, spEndDate)}
              onSelect={onSelectQuarter}
            />
            <BaseSelect
              name="월별검색"
              options={reverseArray(MONTHS)}
              selectedOption={convertDateToPeriod(spStartDate, spEndDate)}
              onSelect={onSelectMonth}
            />
            <BaseInput
              placeholder="거래처·적요 검색"
              onInput={(e) => {
                setKeyword(e.currentTarget.value.trim());
                setPage(0);
              }}
              css={css`
                box-sizing: border-box;
              `}
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 12px;
            `}
          >
            <Tooltip title={releaseDisabledReason}>
              <div>
                <BaseButton
                  variant="outlined"
                  color={PALETTE.primary}
                  disabled={actionDisabled || !!releaseDisabledReason}
                  onClick={() => release(selectedJournals.map((j) => j.id))}
                >
                  보류 해제
                </BaseButton>
              </div>
            </Tooltip>
            <Tooltip title={deleteDisabledReason}>
              <div>
                <BaseButton
                  variant="outlined"
                  color={PALETTE.error}
                  disabled={actionDisabled || !!deleteDisabledReason}
                  onClick={() =>
                    setDeleteTargetIds(selectedJournals.map((j) => j.id))
                  }
                >
                  삭제
                </BaseButton>
              </div>
            </Tooltip>
            <Tooltip title={confirmDisabledReason}>
              <div>
                <BaseButton
                  variant={isPeriodConfirmed ? "outlined" : "contained"}
                  color={PALETTE.primary}
                  // 상태·계정 필터로 목록이 비어도 기간에 전표가 있으면 마감할 수 있다
                  disabled={actionDisabled || !!confirmDisabledReason}
                  onClick={toggleConfirmation}
                >
                  {isPeriodConfirmed ? "마감 해제" : "마감 설정"}
                </BaseButton>
              </div>
            </Tooltip>
          </div>
        </div>

        <div
          css={css`
            margin-bottom: 12px;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            font-size: 16px;
            font-weight: 500;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 24px;
              font-size: 14px;
            `}
          >
            <div
              css={css`
                font-size: 16px;
                font-weight: 700;
              `}
            >
              총 {rows.length}건
            </div>
            <StatusCount
              label="미전송"
              dataNum={summary.unsent}
              amount={summary.unsentAmount}
              isShow={statusFilter === "unsent"}
              showHandler={toggleStatusFilter("unsent")}
            />
            <StatusCount
              label="보류"
              dataNum={summary.held}
              isShow={statusFilter === "held"}
              showHandler={toggleStatusFilter("held")}
            />
            <StatusCount
              label="전송 실패"
              dataNum={summary.failed}
              isShow={statusFilter === "failed"}
              showHandler={toggleStatusFilter("failed")}
            />
            <div
              css={css`
                color: ${isBalancedTotal ? PALETTE.text : PALETTE.error};
              `}
            >
              차변 <strong>{convertCurrency(totalDebit)}</strong>원 · 대변{" "}
              <strong>{convertCurrency(totalCredit)}</strong>원
              {!isBalancedTotal && " (불일치)"}
            </div>
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 16px;
              `}
            >
              <div>
                선택{" "}
                <BaseButton
                  variant={isShowSelectedOnly ? "contained" : "outlined"}
                  color={PALETTE.primary}
                  disabled={checkedIds.size === 0}
                  style={{
                    padding: "2px 6px 1px",
                    fontSize: "13px",
                    transform: "translateY(-1px)"
                  }}
                  onClick={() => setIsShowSelectedOnly((prev) => !prev)}
                >
                  {checkedIds.size}건
                </BaseButton>
              </div>
              <div>
                금액{" "}
                <strong>
                  {convertCurrency(sumBy(selectedJournals, "totalDebit"))}
                </strong>
                원
              </div>
            </div>
          </div>

          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 12px;
            `}
          >
            <BaseSelect
              name="상태"
              color={PALETTE.primary}
              minWidth={120}
              options={STATUS_FILTER_OPTIONS}
              selectedOption={statusFilter}
              onSelect={(e) =>
                setStatusFilter(e.currentTarget.value as JournalNewzenFilter)
              }
              css={css`
                padding: 6px 12px;
              `}
            />
            <BaseSelect
              name="계정"
              color={PALETTE.primary}
              minWidth={160}
              options={[
                { value: ALL_ACCOUNTS, label: "전체 계정" },
                ...Array.from(accountNames.entries())
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([code, name]) => ({
                    value: code,
                    label: `${name} (${code})`
                  }))
              ]}
              selectedOption={accountFilter}
              onSelect={(e) => setAccountFilter(e.currentTarget.value)}
              css={css`
                padding: 6px 12px;
              `}
            />
            <Tooltip title={sendDisabledReason}>
              <div>
                <BaseButton
                  variant="contained"
                  color={PALETTE.primary}
                  disabled={actionDisabled || !!sendDisabledReason}
                  onClick={() => setIsSendOpen(true)}
                >
                  세무사랑{sendableCount > 0 && ` (${sendableCount})`}
                </BaseButton>
              </div>
            </Tooltip>
          </div>
        </div>

        {summary.held > 0 && (
          <div
            css={css`
              margin-bottom: 12px;
              font-size: 14px;
              color: ${PALETTE.warning};
            `}
          >
            보류 {summary.held}건이 있습니다. 확인 후 해제하거나 삭제해 주세요.
          </div>
        )}

        <Table
          isLoading={isLoading}
          minWidth={1240}
          sortLimit={2}
          selectable={true}
          checkedSet={checkedIds}
          setCheckedSet={setCheckedIds}
          columns={JOURNAL_COLS}
          dataList={pageRows}
          clickRow={toggleExpanded}
          expandedIds={expandedIds}
          renderExpanded={(data: JournalRow) => (
            <JournalDetail
              journal={data}
              accountNames={accountNames}
              reviewLink={reviewLink}
              disabled={actionDisabled}
              onRelease={() => release([data.id])}
              onDelete={() => setDeleteTargetIds([data.id])}
            />
          )}
          emptyMessage="조건에 맞는 일반전표가 없습니다"
        />

        {pageCount > 1 && (
          <div
            css={css`
              margin-top: 12px;
              display: flex;
              justify-content: center;
              align-items: center;
              gap: 12px;
              font-size: 14px;
            `}
          >
            <BaseButton
              variant="outlined"
              color={PALETTE.primary}
              disabled={currentPage === 0}
              onClick={() => setPage(currentPage - 1)}
            >
              이전
            </BaseButton>
            <span>
              {currentPage + 1} / {pageCount}
            </span>
            <BaseButton
              variant="outlined"
              color={PALETTE.primary}
              disabled={currentPage >= pageCount - 1}
              onClick={() => setPage(currentPage + 1)}
            >
              다음
            </BaseButton>
          </div>
        )}
      </section>

      <NewzenSendDialog
        isOpen={isSendOpen}
        bizId={bizId}
        journals={selectedJournals}
        onClose={() => setIsSendOpen(false)}
        onFinished={() => {
          loadBusiness();
          loadJournals();
        }}
      />
      <ConfirmDialog
        isOpen={deleteTargetIds.length > 0}
        title="알림"
        disabled={isBusy}
        onCancel={() => setDeleteTargetIds([])}
        onConfirm={confirmDelete}
      >
        <DialogContentText>
          선택한 {deleteTargetIds.length}건을 삭제하시겠습니까?
          <br />
          삭제한 전표는 복구할 수 없습니다.
        </DialogContentText>
      </ConfirmDialog>
      <AlertDialog
        isOpen={isAlertOpen}
        setIsOpen={setIsAlertOpen}
        alertData={alertData}
      />
    </div>
  );
}

// Review의 미처리·미전송 표시(UnprocessStatus)와 같은 모양. 누르면 그 상태만 본다
function StatusCount({
  label,
  dataNum,
  amount,
  isShow,
  showHandler
}: {
  label: string;
  dataNum: number;
  amount?: number;
  isShow: boolean;
  showHandler: () => void;
}) {
  return (
    <div>
      {label}{" "}
      {dataNum > 0 || isShow ? (
        <BaseButton
          variant={isShow ? "contained" : "outlined"}
          color={PALETTE.error}
          style={{
            padding: "2px 6px 1px",
            fontSize: "13px",
            transform: "translateY(-1px)"
          }}
          onClick={showHandler}
        >
          {dataNum}건
        </BaseButton>
      ) : (
        <span>{dataNum}건</span>
      )}
      {!!amount && <> ({convertCurrency(amount)}원)</>}
    </div>
  );
}

function JournalDetail({
  journal,
  accountNames,
  reviewLink,
  disabled,
  onRelease,
  onDelete
}: {
  journal: Journal;
  accountNames: AccountNameMap;
  reviewLink: (menuType: string) => string;
  disabled: boolean;
  onRelease: () => void;
  onDelete: () => void;
}) {
  const status = journalStatus(journal);
  // 출금·입금은 side가 없으므로 화면 기준(출금=차변, 입금=대변)으로 표시
  const lineSide = (line: Journal["lines"][number]) =>
    line.side || (linesBySide(journal, "D").includes(line) ? "D" : "C");
  const lockedReason = journal.isAccountingConfirmed
    ? CONFIRMED_SELECTION_MESSAGE
    : "";

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 12px;
        font-size: 14px;
        color: ${PALETTE.text};
      `}
    >
      <table
        css={css`
          width: 100%;
          border-collapse: collapse;
          background-color: ${PALETTE.light};
          border-radius: 8px;

          th,
          td {
            height: 36px;
            padding: 0 10px;
            text-align: left;
          }
          th {
            font-weight: 500;
            background-color: ${PALETTE.backgroundDisabled};
          }
          td {
            border-top: 1px solid ${PALETTE.borderLight};
          }
        `}
      >
        <thead>
          <tr>
            <th
              css={css`
                width: 48px;
              `}
            >
              차/대
            </th>
            <th
              css={css`
                width: 200px;
              `}
            >
              계정
            </th>
            <th
              css={css`
                width: 200px;
              `}
            >
              거래처
            </th>
            <th>적요</th>
            <th
              css={css`
                width: 120px;
                text-align: right !important;
              `}
            >
              금액
            </th>
          </tr>
        </thead>
        <tbody>
          {journal.lines.map((line) => (
            <tr key={line.lineNo}>
              <td>{lineSide(line) === "D" ? "차변" : "대변"}</td>
              <td>{accountLabel(line.accountCode, accountNames)}</td>
              <td>
                {line.storeName || "-"}
                {line.storeCode ? ` (${line.storeCode})` : ""}
              </td>
              <td>{line.memo || "-"}</td>
              <td
                css={css`
                  text-align: right !important;
                `}
              >
                {convertCurrency(line.amount)}
              </td>
            </tr>
          ))}
          {journal.slipType !== "3" && (
            <tr>
              <td>{journal.slipType === "1" ? "대변" : "차변"}</td>
              <td
                colSpan={4}
                css={css`
                  color: ${PALETTE.textMid};
                `}
              >
                현금 (세무사랑이 상대계정을 붙임)
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div
        css={css`
          display: grid;
          grid-template-columns: 120px 1fr;
          row-gap: 6px;
        `}
      >
        <DetailLabel>연결 원천</DetailLabel>
        <div
          css={css`
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
          `}
        >
          {journal.refs?.length
            ? journal.refs.map((ref) =>
                ref.missing ? (
                  <span
                    key={ref.id}
                    css={css`
                      color: ${PALETTE.error};
                    `}
                  >
                    원천 없음
                  </span>
                ) : ref.type === "bank" ? (
                  <RefLink key={ref.id} href={reviewLink("bank")}>
                    통장
                  </RefLink>
                ) : ref.extraType ? (
                  <RefLink
                    key={ref.id}
                    href={reviewLink(
                      ref.extraType === "BUY" ? "extra-buy" : "extra-sales"
                    )}
                  >
                    {ref.extraType === "BUY" ? "기타매입" : "기타매출"}
                  </RefLink>
                ) : (
                  // 구버전 응답처럼 extraType이 없으면 두 탭을 다 보여 준다
                  <span key={ref.id}>
                    <RefLink href={reviewLink("extra-buy")}>기타매입</RefLink>
                    {" / "}
                    <RefLink href={reviewLink("extra-sales")}>기타매출</RefLink>
                  </span>
                )
              )
            : "-"}
        </div>
        <DetailLabel>externalKey</DetailLabel>
        <div>{journal.externalKey || "-"}</div>
        <DetailLabel>메모</DetailLabel>
        <div>{journal.memo || "-"}</div>
        <DetailLabel>등록자</DetailLabel>
        <div>
          {journal.createdByUser || "-"}
          {journal.createdDate && ` (${journal.createdDate.replace("T", " ")})`}
        </div>
        {status === "sent" && (
          <>
            <DetailLabel>세무사랑 전송</DetailLabel>
            <div>
              {journal.newzenSlipNo
                ? `전표번호 #${journal.newzenSlipNo} · `
                : ""}
              {journal.newzenSentAt
                ? journal.newzenSentAt.replace("T", " ").slice(0, 19)
                : "-"}
            </div>
          </>
        )}
        {status === "failed" && (
          <>
            <DetailLabel>전송 실패</DetailLabel>
            <div
              css={css`
                color: ${PALETTE.error};
              `}
            >
              [{journal.newzenErrorRule}] {journal.newzenErrorMessage}
            </div>
          </>
        )}
        {status === "held" && (
          <>
            <DetailLabel>보류 사유</DetailLabel>
            <div>
              <div
                css={css`
                  color: ${PALETTE.warning};
                  font-weight: 500;
                `}
              >
                {holdReasonLabel(journal.holdReason)}
              </div>
              {journal.holdNote && (
                <div
                  css={css`
                    white-space: pre-line;
                  `}
                >
                  {journal.holdNote}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {status === "held" && (
        <div
          css={css`
            display: flex;
            gap: 12px;
          `}
        >
          <Tooltip title={lockedReason}>
            <div>
              <BaseButton
                variant="outlined"
                color={PALETTE.primary}
                disabled={disabled || !!lockedReason}
                onClick={onRelease}
              >
                보류 해제
              </BaseButton>
            </div>
          </Tooltip>
          <Tooltip title={lockedReason}>
            <div>
              <BaseButton
                variant="outlined"
                color={PALETTE.error}
                disabled={disabled || !!lockedReason}
                onClick={onDelete}
              >
                삭제
              </BaseButton>
            </div>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

function DetailLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      css={css`
        color: ${PALETTE.textMid};
      `}
    >
      {children}
    </div>
  );
}

function RefLink({
  href,
  children
}: {
  href: string;
  children: React.ReactNode;
}) {
  // 항목 단위 이동이 없어 그 사업자의 탭까지만 새 창으로 연다
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      css={css`
        color: ${PALETTE.primary};
      `}
    >
      {children}
    </a>
  );
}

export default JournalReview;
